/**
 * Pure transform: a model-supplied `ChartSpec` → Highcharts `Options`. Kept free of
 * React and the assistant-ui runtime so it can be unit-tested in isolation.
 */
import type { Options } from "highcharts";
import { assignChartColors } from "./chartColors";
import type { ChartSpec } from "./chartSpec";

/**
 * What carries a colour, per chart type. A pie is ONE series whose points are the things being
 * compared, so its colours are per point; every other type compares series, so they are per series.
 * Returning the labels (rather than the colours) keeps the "which axis of the chart is
 * colour-bearing" decision here, next to the Highcharts shape it has to fit.
 */
function colourBearingLabels(spec: ChartSpec): string[] {
	if (spec.chartType !== "pie") return spec.series.map((s) => s.name);
	// A pie's own series name labels the whole ring, not any wedge; the wedges are the named points.
	// Bare numbers can't be matched to a concept, so they fall through to the palette.
	return (spec.series[0]?.data ?? []).map((point) => (typeof point === "number" ? "" : point.name));
}

export function buildChartOptions(spec: ChartSpec): Options {
	const isStackable = spec.chartType === "column" || spec.chartType === "bar" || spec.chartType === "area";

	// Colours the product already associates with a label win; the rest come from the chart palette.
	// Set as the chart-level `colors` so Highcharts applies them positionally to whichever axis is
	// colour-bearing — series for most types, points for a pie — with no per-point rewriting.
	const colors = assignChartColors(colourBearingLabels(spec));

	return {
		credits: { enabled: false },
		colors,
		// No animation: the options object is rebuilt on every streamed token of the tool's arguments,
		// so an animated draw/redraw restarts continuously while the model writes the spec.
		chart: {
			type: spec.chartType,
			animation: false,
			backgroundColor: "transparent",
			style: { fontFamily: "inherit" },
		},
		title: spec.title ? { text: spec.title } : { text: undefined },
		subtitle: spec.subtitle ? { text: spec.subtitle } : undefined,
		xAxis:
			spec.chartType === "pie"
				? undefined
				: {
						categories: spec.categories,
						title: spec.xAxisTitle ? { text: spec.xAxisTitle } : undefined,
					},
		yAxis:
			spec.chartType === "pie"
				? undefined
				: {
						title: spec.yAxisTitle ? { text: spec.yAxisTitle } : undefined,
					},
		legend: { enabled: spec.series.length > 1 || spec.chartType === "pie" },
		plotOptions: {
			series: { animation: false, stacking: spec.stacked && isStackable ? "normal" : undefined },
			pie: { dataLabels: { enabled: true } },
		},
		// The spec's series shape (name + numbers/{name,y}) lines up with Highcharts'
		// series options; the cast narrows the union to the per-type series config.
		series: spec.series.map((s) => ({ type: spec.chartType, name: s.name, data: s.data })) as Options["series"],
	};
}
